import { CatalogPermissionService } from '@aipb/gulliver/catalog/auth';
import { DropboxService } from '@aipb/gulliver/dropbox/data-access';
import { ImageQualityInfoResponse, ImageQualityParams, ImageRaw, ResolutionModel } from '@aipb/gulliver/dropbox/model';
import { saveFileFromUrl } from '@aipb/shared/util/other';
import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'ip-download',
  templateUrl: './download.component.html',
  styleUrls: ['./download.component.scss']
})
export class DownloadComponent implements OnChanges {
  @Input() resolutionData: ImageQualityInfoResponse;
  @Input() id: string;
  @Input() imageRaw: ImageRaw;
  @Input() isDropbox: boolean;

  @Output() refresh = new EventEmitter<void>();

  public dropboxDownloadLoading = false;
  downloadsLoading: { [key: string]: boolean } = {};

  message: string = null;
  messageClass = '';

  constructor(
    private dropbox: DropboxService,
    private transloco: TranslocoService,
    private catalogPermission: CatalogPermissionService
  ) {}

  get resolutions(): ResolutionModel[] {
    if (!this.resolutionData) return [];

    return this.resolutionData.resAvailable;
  }

  get freeDownloadsLeft(): number {
    if (!this.resolutionData) return 0;

    return this.resolutionData.freeDownloadsLeft;
  }

  get paidDownloadsAllowed(): boolean {
    if (!this.resolutionData) return false;

    return this.resolutionData.paidDownloadsAllowed;
  }

  get lang() {
    return this.transloco.getActiveLang();
  }

  ngOnChanges() {
    this.initMessage();
  }

  onResourceDownload(res: ResolutionModel): void {
    this.downloadsLoading[res.id] = true;

    const params: ImageQualityParams = {
      res: this.id,
      q: res.id,
      lang: this.transloco.getActiveLang()
    };

    this.dropbox
      .imageQuality(params)
      .pipe(
        finalize(() => {
          this.downloadsLoading[res.id] = false;
          this.refresh.emit();
        })
      )
      .subscribe((data) => {
        const image = URL.createObjectURL(data[0]);
        saveFileFromUrl(image, data[1][0], data[1][1]);
      });
  }

  onDownloadDropboxImage(): void {
    if (this.dropboxDownloadLoading) return;

    this.dropboxDownloadLoading = true;
    this.dropbox
      .imageDownload(this.id)
      .pipe(
        finalize(() => {
          this.dropboxDownloadLoading = false;
        }),
        untilDestroyed(this)
      )
      .subscribe((data) => {
        const image = URL.createObjectURL(data[0]);
        saveFileFromUrl(image, data[1][0], data[1][1]);
      });
  }

  onDownloadImage(): void {
    const image = URL.createObjectURL(this.imageRaw[0]);
    saveFileFromUrl(image, this.imageRaw[1][0], this.imageRaw[1][1]);
  }

  hasDownloadRole(): boolean {
    return this.catalogPermission.hasDownloadRole;
  }

  hasDownloadInternalRole(): boolean {
    return this.catalogPermission.hasDownloadInternalRole;
  }

  public canDownloadFromArchive(): boolean {
    return !this.isDropbox /* && 'DFotoHiRes' in this.data.item*/;
  }

  private initMessage(): void {
    if (!this.hasDownloadRole()) {
      this.message = this.transloco.translate('catalog.image_preview.messages.no_download_role');
      this.messageClass = '';
      return;
    }

    if (!this.isDropbox) {
      this.message = this.canDownloadFromArchive()
        ? this.transloco.translate('catalog.image_preview.messages.can_download_from_archive')
        : this.transloco.translate('catalog.image_preview.messages.cant_download_from_archive');
      this.messageClass = this.canDownloadFromArchive() ? '' : 'text-warn';
      return;
    }

    if (this.hasDownloadInternalRole()) {
      this.message = this.transloco.translate('catalog.image_preview.messages.can_directly_download_from_dropbox');
      this.messageClass = '';
      return;
    }

    if (this.resolutionData.alreadyDownloaded) {
      this.message = this.transloco.translate('catalog.image_preview.messages.already_downloaded');
      this.messageClass = '';
      return;
    }

    if (this.resolutionData.freeDownloadsLeft > 0) {
      this.message = `${this.transloco.translate('catalog.image_preview.messages.free_downloads_left')}: ${
        this.resolutionData.freeDownloadsLeft
      }`;
      this.messageClass = '';
      return;
    }

    if (this.resolutionData.freeDownloadsLeft <= 0 && this.resolutionData.paidDownloadsAllowed) {
      this.message = this.transloco.translate('catalog.image_preview.messages.paid_downloads_allowed_no_free_left');
      this.messageClass = 'text-warn';
      return;
    }

    if (this.resolutionData.freeDownloadsLeft <= 0) {
      this.message = this.transloco.translate('catalog.image_preview.messages.no_free_downloads_left');
      this.messageClass = '';
      return;
    }
  }
}
